// MCP Security — Settings landing page.
//
// Entry point for per-endpoint policy tuning. Lists every registered
// MCP endpoint on the current site with a one-line policy summary; each
// row navigates to /v2/mcp/settings/:endpointId for the full form.
//
// Mounted at /v2/mcp/settings — a dedicated submenu item alongside
// Overview, Events, Configuration, Onboarding.

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Typography,
  Box,
  Alert,
  Button,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";

import SlidersHorizontalIcon from "@mui/icons-material/TuneOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";

import V2PageHeader from "../../../../common/V2PageHeader";
import axios from "../../../../../utils/axios/v1/userAxios";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import { SectionCard, FG1, FG3, BORDER } from "./Config";
import { primaryButtonStyle, secondaryButtonStyle, tableHeadSx, tableBodySx } from "./_shared/buttonStyles";

function modeChip(label, value) {
  const normalized = (value || "").toLowerCase();
  const colour =
    normalized === "block" || normalized === "enforce" || normalized === "redact"
      ? { bg: "#FEE4E4", fg: "#8A0F0F", border: "#F3B9B9" }
      : normalized === "log_only"
      ? { bg: "#FFF2E3", fg: "#7A4D00", border: "#F2C779" }
      : { bg: "#EEF0F6", fg: "#5A6076", border: "#D7DBE6" };
  return (
    <Chip
      size="small"
      label={`${label}: ${value || "—"}`}
      sx={{
        background: colour.bg,
        color: colour.fg,
        border: `1px solid ${colour.border}`,
        fontSize: 10.5,
        height: 20,
        letterSpacing: 0.2,
        fontFamily: "monospace",
      }}
    />
  );
}

export default function McpSettingsIndex() {
  const { effectiveSiteUid } = useGlobalSite();
  const navigate = useNavigate();
  const [endpoints, setEndpoints] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!effectiveSiteUid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`mcp/sites/${effectiveSiteUid}/endpoints`);
        if (!cancelled) setEndpoints(r.data.items || []);
      } catch (e) {
        if (!cancelled) {
          setEndpoints([]);
          setErr(e?.response?.data?.message || e.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveSiteUid]);

  return (
    <>
      <Helmet>
        <title>MCP Security · Settings</title>
      </Helmet>
      <V2PageHeader
        icon={SlidersHorizontalIcon}
        title="MCP Security — Settings"
        description="Tune per-endpoint policy: enforcement modes, caps, and whether the generic WAF stack re-enables on an MCP location."
        actions={
          <Button
            sx={secondaryButtonStyle}
            startIcon={<AutoFixHighOutlinedIcon />}
            onClick={() => navigate("/v2/mcp/onboard")}
          >
            Onboard new endpoint
          </Button>
        }
      />

      {!effectiveSiteUid && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Select a site from the header to see endpoint settings.
        </Alert>
      )}

      {effectiveSiteUid && endpoints == null && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {err && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {err}
        </Alert>
      )}

      {effectiveSiteUid && endpoints && endpoints.length === 0 && (
        <SectionCard
          icon={<SlidersHorizontalIcon />}
          title="No endpoints to tune yet"
          desc="There are no MCP endpoints registered on this site. Run through onboarding to add one."
          right={
            <Button
              sx={primaryButtonStyle}
              startIcon={<AutoFixHighOutlinedIcon />}
              onClick={() => navigate("/v2/mcp/onboard")}
            >
              Start onboarding
            </Button>
          }
        />
      )}

      {effectiveSiteUid && endpoints && endpoints.length > 0 && (
        <SectionCard
          icon={<SlidersHorizontalIcon />}
          title="Endpoints on this site"
          desc="Click an endpoint to edit its enforcement modes, caps, and generic-module overrides."
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadSx}>
                  Endpoint
                </TableCell>
                <TableCell sx={tableHeadSx}>
                  State
                </TableCell>
                <TableCell sx={tableHeadSx}>
                  Current modes
                </TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {endpoints.map((ep) => (
                <TableRow
                  key={ep.endpoint_id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/v2/mcp/settings/${ep.endpoint_id}`)}
                >
                  <TableCell>
                    <Typography sx={{ ...tableBodySx, color: FG1 }}>
                      {ep.location_pattern || ep.endpoint_url}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, color: FG3, fontFamily: "monospace" }}
                    >
                      {ep.endpoint_url} · {ep.transport}
                    </Typography>
                  </TableCell>
                  <TableCell sx={tableBodySx}>
                    {ep.state}
                    {ep.log_only ? " · log-only" : ""}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {modeChip("inj", ep.injection_mode)}
                      {modeChip("ses", ep.session_mode)}
                      {modeChip("cred", ep.credential_mode)}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="text"
                      endIcon={<ChevronRightOutlinedIcon fontSize="small" />}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography sx={{ fontSize: 11, color: FG3, mt: 1.5, borderTop: `1px solid ${BORDER}`, pt: 1.5 }}>
            Need to enable, reprobe, or disable an endpoint? Those live on the <b>Configuration</b> page.
            This page is for policy knobs only.
          </Typography>
        </SectionCard>
      )}
    </>
  );
}
